import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid2,
    Container,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAdvancedSalesReport } from '../../features/StockReports/salesReportSlice';
import Navbar from '../setup/Navbar';
import HeadingText from '../../BuiltInComponent/HeadingTest';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import PrintIcon from '@mui/icons-material/Print';

const defaultFilters = {
    scope: 'today',
    fromDate: '',
    toDate: '',
    selectedUserID: 'all',
    orderType: 'all',
    orderStatus: 'all',
    paymentMethod: 'all',
    minAmount: '',
    maxAmount: '',
    search: ''
};

const THERMAL_PREVIEW_WIDTH = 42;

const thermalClamp = (value, max = THERMAL_PREVIEW_WIDTH) => {
    const text = String(value ?? '');
    if (text.length <= max) return text;
    if (max <= 3) return text.slice(0, max);
    return `${text.slice(0, max - 3)}...`;
};

const thermalCenter = (value, width = THERMAL_PREVIEW_WIDTH) => {
    const text = thermalClamp(value, width);
    const totalPadding = Math.max(0, width - text.length);
    const left = Math.floor(totalPadding / 2);
    const right = totalPadding - left;
    return `${' '.repeat(left)}${text}${' '.repeat(right)}`;
};

const thermalLeftRight = (left, right, width = THERMAL_PREVIEW_WIDTH) => {
    const rightText = thermalClamp(String(right ?? ''), width);
    const maxLeftLength = Math.max(0, width - rightText.length - 1);
    const leftText = thermalClamp(String(left ?? ''), maxLeftLength || width);
    const spacing = width - leftText.length - rightText.length;

    if (spacing < 1) {
        return `${thermalClamp(leftText, width - rightText.length - 1)} ${rightText}`;
    }

    return `${leftText}${' '.repeat(spacing)}${rightText}`;
};

const thermalWrap = (value, width = THERMAL_PREVIEW_WIDTH) => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return [];

    const words = text.split(' ');
    const lines = [];
    let current = '';

    words.forEach((word) => {
        if (!current) {
            current = word;
            return;
        }

        if ((current.length + 1 + word.length) <= width) {
            current += ` ${word}`;
        } else {
            lines.push(current);
            current = word;
        }
    });

    if (current) lines.push(current);
    return lines;
};

const SalesReport = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const { reportRows, summary, paymentMethodOptions, userOptions, itemConsumptionSummary, loading, error } = useSelector((state) => state.sr);

    const [filters, setFilters] = useState(defaultFilters);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [expandedOrders, setExpandedOrders] = useState({});
    const [printPreviewOpen, setPrintPreviewOpen] = useState(false);

    useEffect(() => {
        if (!user?.userID) return;
        dispatch(getAdvancedSalesReport({
            userID: user.userID,
            ...defaultFilters
        }));
    }, [dispatch, user]);

    const visibleRows = useMemo(
        () => reportRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [reportRows, page, rowsPerPage]
    );

    const onChangeFilter = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        if (!user?.userID) return;
        setPage(0);
        dispatch(getAdvancedSalesReport({ userID: user.userID, ...filters }));
    };

    const resetFilters = () => {
        const resetPayload = { ...defaultFilters };
        setFilters(resetPayload);
        setPage(0);
        setExpandedOrders({});
        if (!user?.userID) return;
        dispatch(getAdvancedSalesReport({ userID: user.userID, ...resetPayload }));
    };

    const handleToggleOrder = (orderID) => {
        setExpandedOrders((prev) => ({
            ...prev,
            [orderID]: !prev[orderID]
        }));
    };

    const handleExpandAllOrders = () => {
        const expandedMap = {};
        visibleRows.forEach((row) => {
            expandedMap[row.orderID] = true;
        });
        setExpandedOrders(expandedMap);
    };

    const handleToggleAllOrders = () => {
        const allOpen = visibleRows.length > 0 && visibleRows.every((row) => !!expandedOrders[row.orderID]);

        if (allOpen) {
            setExpandedOrders({});
            return;
        }

        handleExpandAllOrders();
    };

    const exportCSV = () => {
        if (!reportRows.length) return;

        const headers = [
            'Order ID',
            'Date',
            'Created At',
            'Completed At',
            'Order Type',
            'Order Status',
            'KOT #',
            'Ticket #',
            'Customer',
            'Phone',
            'Table',
            'Payment Methods',
            'Amount',
            'Aggregator',
            'Aggregator Order #',
            'Last Bill Printed At'
        ];

        const escapeValue = (value) => {
            const str = String(value ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const lines = reportRows.map((row) => [
            row.orderID,
            row.entDate ? dayjs(row.entDate).format('YYYY-MM-DD') : '',
            row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
            row.completedAt ? dayjs(row.completedAt).format('YYYY-MM-DD HH:mm:ss') : '',
            row.orderType,
            row.orderStatus,
            row.kotNumber,
            row.ticketNo,
            row.customerName,
            row.customerPhone,
            row.tableLabel,
            row.paymentMethods,
            Number(row.grandTotal || 0).toFixed(2),
            row.aggregatorPlatform,
            row.aggregatorOrderCode,
            row.lastBillPrintedAt ? dayjs(row.lastBillPrintedAt).format('YYYY-MM-DD HH:mm:ss') : ''
        ].map(escapeValue).join(','));

        const csvContent = [headers.join(','), ...lines].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `sales-report-${dayjs().format('YYYYMMDD-HHmm')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const amount = (value) => `Rs. ${Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;

    const areAllOrdersOpen = visibleRows.length > 0 && visibleRows.every((row) => !!expandedOrders[row.orderID]);

    const itemWiseTotalSalesSummary = useMemo(
        () => (itemConsumptionSummary || []).map((item) => ({
            itemCode: item.itemCode,
            itemName: item.itemName,
            reportingUnitName: item.reportingUnitName || 'Unit',
            totalQty: Number(item.totalEquivalentReportingQty || 0),
        })),
        [itemConsumptionSummary]
    );

    const thermalPreviewText = useMemo(() => {
        const width = THERMAL_PREVIEW_WIDTH;
        const lines = [];
        const divider = '='.repeat(width);

        lines.push(thermalCenter('Lucky Braost', width));
        lines.push(thermalCenter('Sales Report', width));
        lines.push(divider);
        lines.push(thermalLeftRight('Printed', dayjs().format('DD-MMM HH:mm'), width));
        lines.push(thermalLeftRight('Scope', filters.scope || 'today', width));
        if (filters.fromDate) {
            lines.push(thermalLeftRight('From', filters.fromDate, width));
        }
        if (filters.toDate) {
            lines.push(thermalLeftRight('To', filters.toDate, width));
        }
        if (filters.orderType && filters.orderType !== 'all') {
            lines.push(thermalLeftRight('Type', filters.orderType, width));
        }
        if (filters.orderStatus && filters.orderStatus !== 'all') {
            lines.push(thermalLeftRight('Status', filters.orderStatus, width));
        }
        lines.push(divider);
        lines.push(thermalCenter('ORDER SUMMARY', width));
        lines.push(divider);
        lines.push(thermalLeftRight('Total Orders', summary.totalOrders || 0, width));
        lines.push(thermalLeftRight('Completed', summary.completedOrders || 0, width));
        lines.push(thermalLeftRight('Active/Held', summary.activeOrders || 0, width));
        lines.push(thermalLeftRight('Draft', summary.draftOrders || 0, width));
        lines.push(thermalLeftRight('Total Amount', amount(summary.totalAmount), width));
        lines.push(thermalLeftRight('Avg Ticket', amount(summary.averageOrderAmount), width));
        lines.push(divider);
        lines.push(thermalCenter('SALES BY TYPE', width));
        lines.push(divider);
        lines.push(thermalLeftRight('Dine In', amount(summary.dineInAmount), width));
        lines.push(thermalLeftRight('Takeaway', amount(summary.takeawayAmount), width));
        lines.push(thermalLeftRight('Dispatch', amount(summary.dispatchAmount), width));
        lines.push(thermalLeftRight('Delivery', amount(summary.deliveryAmount), width));
        lines.push(thermalLeftRight('Foodpanda', amount(summary.foodpandaAmount), width));
        lines.push(divider);
        lines.push(thermalCenter('ITEM WISE TOTAL SALES', width));
        lines.push(divider);

        if (itemWiseTotalSalesSummary.length === 0) {
            lines.push('No item wise sales data');
        } else {
            itemWiseTotalSalesSummary.forEach((item) => {
                lines.push(
                    thermalLeftRight(
                        String(item.itemName || '-').toUpperCase(),
                        `${item.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${item.reportingUnitName}`,
                        width
                    )
                );
                lines.push(divider);
            });
        }

        lines.push(thermalCenter('ITEM CONSUMPTION', width));
        lines.push(divider);

        if ((itemConsumptionSummary || []).length === 0) {
            lines.push('No item consumption data');
        } else {
            itemConsumptionSummary.forEach((item) => {
                lines.push(thermalClamp(String(item.itemName || '-').toUpperCase(), width));
                lines.push(
                    thermalLeftRight(
                        `Sep ${Number(item.separateSoldQty || 0).toLocaleString()}`,
                        `${Number(item.dealBaseUnitQty || 0).toLocaleString()} ${item.baseUnitName || 'Unit'}`,
                        width
                    )
                );
                lines.push(
                    thermalLeftRight(
                        `Deal Eqv ${Number(item.dealEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                        `Tot ${Number(item.totalEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                        width
                    )
                );
                thermalWrap(
                    `${Number(item.baseUnitsPerReportingUnit || 1)} ${item.baseUnitName || 'Unit'} = 1 ${item.reportingUnitName || 'Unit'}`,
                    width
                ).forEach((line) => lines.push(line));
                lines.push(divider);
            });
        }

        lines.push(thermalCenter('PREVIEW ONLY', width));
        return lines.join('\n');
    }, [filters, itemConsumptionSummary, itemWiseTotalSalesSummary, summary]);

    const handleOpenPrintPreview = () => {
        if (!reportRows.length) return;
        setPrintPreviewOpen(true);
    };

    const handleClosePrintPreview = () => {
        setPrintPreviewOpen(false);
    };

    const handlePrintThermal = () => {
        const popup = window.open('', '_blank', 'width=420,height=760');
        if (!popup) return;

        const escapedText = thermalPreviewText
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        popup.document.write(`
            <html>
                <head>
                    <title>Sales Report Thermal Print</title>
                    <style>
                        @page { size: 80mm auto; margin: 4mm; }
                        body { margin: 0; padding: 0; font-family: 'Courier New', monospace; }
                        .sheet { width: 72mm; margin: 0 auto; padding: 4mm 0; }
                        pre { margin: 0; white-space: pre-wrap; word-break: break-word; font-size: 11px; line-height: 1.35; }
                    </style>
                </head>
                <body>
                    <div class="sheet"><pre>${escapedText}</pre></div>
                    <script>
                        window.onload = function() {
                            window.focus();
                            window.print();
                        };
                    </script>
                </body>
            </html>
        `);
        popup.document.close();
    };

    const handleDownloadThermalPdf = () => {
        const lines = thermalPreviewText.split('\n');
        const lineHeight = 4;
        const topMargin = 6;
        const bottomMargin = 6;
        const pageHeight = Math.max(80, topMargin + bottomMargin + (lines.length * lineHeight));

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [80, pageHeight]
        });

        doc.setFont('courier', 'normal');
        doc.setFontSize(8.5);

        let y = topMargin;
        lines.forEach((line) => {
            doc.text(line, 4, y);
            y += lineHeight;
        });

        doc.save(`sales-report-thermal-${dayjs().format('YYYYMMDD-HHmm')}.pdf`);
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="xl">
                <Box sx={{ p: 2 }}>
                    <HeadingText text="Sales Report" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Advanced order-level sales report with completed and active order visibility.
                    </Typography>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Advanced Filters</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            startIcon={<FilterAltIcon />}
                                            onClick={applyFilters}
                                            disabled={loading}
                                        >
                                            Apply
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<RestartAltIcon />}
                                            onClick={resetFilters}
                                            disabled={loading}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DownloadForOfflineIcon />}
                                            onClick={exportCSV}
                                            disabled={!reportRows.length}
                                        >
                                            Export CSV
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleToggleAllOrders}
                                            disabled={!visibleRows.length}
                                        >
                                            {areAllOrdersOpen ? 'Hide All Orders' : 'Show All Orders'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<PrintIcon />}
                                            onClick={handleOpenPrintPreview}
                                            disabled={!reportRows.length}
                                        >
                                            Print / PDF
                                        </Button>
                                    </Stack>
                                </Stack>

                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Scope</InputLabel>
                                            <Select
                                                label="Scope"
                                                value={filters.scope}
                                                onChange={(e) => onChangeFilter('scope', e.target.value)}
                                            >
                                                <MenuItem value="today">Today</MenuItem>
                                                <MenuItem value="lastDay">Last Day</MenuItem>
                                                <MenuItem value="last7days">Last 7 Days</MenuItem>
                                                <MenuItem value="last30days">Last 30 Days</MenuItem>
                                                <MenuItem value="opening">Current Opening</MenuItem>
                                                <MenuItem value="custom">Custom Range</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label="From Date"
                                            value={filters.fromDate}
                                            onChange={(e) => onChangeFilter('fromDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label="To Date"
                                            value={filters.toDate}
                                            onChange={(e) => onChangeFilter('toDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Order Type</InputLabel>
                                            <Select
                                                label="Order Type"
                                                value={filters.orderType}
                                                onChange={(e) => onChangeFilter('orderType', e.target.value)}
                                            >
                                                <MenuItem value="all">All</MenuItem>
                                                <MenuItem value="dinein">Dine In</MenuItem>
                                                <MenuItem value="takeaway">Takeaway</MenuItem>
                                                <MenuItem value="dispatch">Dispatch</MenuItem>
                                                <MenuItem value="delivery">Delivery</MenuItem>
                                                <MenuItem value="foodpanda">Foodpanda</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>User</InputLabel>
                                            <Select
                                                label="User"
                                                value={filters.selectedUserID}
                                                onChange={(e) => onChangeFilter('selectedUserID', e.target.value)}
                                            >
                                                <MenuItem value="all">All Users</MenuItem>
                                                {(userOptions || []).map((option) => (
                                                    <MenuItem key={option.value} value={String(option.value)}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                label="Status"
                                                value={filters.orderStatus}
                                                onChange={(e) => onChangeFilter('orderStatus', e.target.value)}
                                            >
                                                <MenuItem value="all">All (Except Cancelled)</MenuItem>
                                                <MenuItem value="completed">Completed</MenuItem>
                                                <MenuItem value="active">Active / Held</MenuItem>
                                                <MenuItem value="draft">Draft</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Payment Method</InputLabel>
                                            <Select
                                                label="Payment Method"
                                                value={filters.paymentMethod}
                                                onChange={(e) => onChangeFilter('paymentMethod', e.target.value)}
                                            >
                                                <MenuItem value="all">All Methods</MenuItem>
                                                {paymentMethodOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Search"
                                            placeholder="Order, KOT, customer, phone, table"
                                            value={filters.search}
                                            onChange={(e) => onChangeFilter('search', e.target.value)}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Min Amount"
                                            value={filters.minAmount}
                                            onChange={(e) => onChangeFilter('minAmount', e.target.value)}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Max Amount"
                                            value={filters.maxAmount}
                                            onChange={(e) => onChangeFilter('maxAmount', e.target.value)}
                                        />
                                    </Grid2>
                                </Grid2>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Grid2 container spacing={2} sx={{ mt: 1 }}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card><CardContent><Typography variant="caption">Total Orders</Typography><Typography variant="h6">{summary.totalOrders || 0}</Typography></CardContent></Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card><CardContent><Typography variant="caption">Total Amount</Typography><Typography variant="h6">{amount(summary.totalAmount)}</Typography></CardContent></Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card><CardContent><Typography variant="caption">Completed</Typography><Typography variant="h6">{summary.completedOrders || 0}</Typography></CardContent></Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card><CardContent><Typography variant="caption">Active/Held</Typography><Typography variant="h6">{summary.activeOrders || 0}</Typography></CardContent></Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card><CardContent><Typography variant="caption">Average Ticket</Typography><Typography variant="h6">{amount(summary.averageOrderAmount)}</Typography></CardContent></Card>
                        </Grid2>
                    </Grid2>

                    <Paper sx={{ mt: 2 }}>
                        <Box sx={{ p: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Item Wise Total Sales</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Har item ki sirf final total sold quantity.
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Total Sale</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itemWiseTotalSalesSummary.length > 0 ? (
                                            itemWiseTotalSalesSummary.map((item) => (
                                                <TableRow key={`total-sales-${item.itemCode || item.itemName}`}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.itemName}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Unit: {item.reportingUnitName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {item.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.reportingUnitName}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    Selected filters ke liye item wise total sales data available nahi hai.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>

                    <Paper sx={{ mt: 2 }}>
                        <Box sx={{ p: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Item Consumption Summary</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Separate sale aur deals dono ko base unit aur equivalent plate level par break down kiya gaya hai.
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Separate</TableCell>
                                            <TableCell align="right">Deals</TableCell>
                                            <TableCell align="right">Deal Plate Eqv.</TableCell>
                                            <TableCell align="right">Total Base</TableCell>
                                            <TableCell align="right">Total Plate Eqv.</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(itemConsumptionSummary || []).length > 0 ? (
                                            itemConsumptionSummary.map((item) => (
                                                <TableRow key={`${item.itemCode || item.itemName}`}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.itemName}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.baseUnitsPerReportingUnit} {item.baseUnitName} = 1 {item.reportingUnitName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {Number(item.separateSoldQty || 0).toLocaleString('en-US')} {item.reportingUnitName}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {Number(item.dealBaseUnitQty || 0).toLocaleString('en-US')} {item.baseUnitName}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {Number(item.dealEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {Number(item.totalBaseUnitQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.baseUnitName}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {Number(item.totalEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.reportingUnitName}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    Selected filters ke liye item consumption data available nahi hai.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>

                    <Paper sx={{ mt: 2 }}>
                        <Box sx={{ p: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Order-Level Sales Report</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Same operational data as POS reprint flow, presented in report format.
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />

                            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                            {loading && (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">Loading report...</Typography>
                                </Stack>
                            )}

                            <TableContainer sx={{ maxHeight: '65vh' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Action</TableCell>
                                            <TableCell>Order</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>KOT / Ticket</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Payment</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell>Print</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleRows.map((row) => {
                                            const isFoodpanda = String(row.aggregatorPlatform || '').toLowerCase() === 'foodpanda';
                                            const isOpen = !!expandedOrders[row.orderID];
                                            return (
                                                <React.Fragment key={row.orderID}>
                                                    <TableRow hover>
                                                        <TableCell>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => handleToggleOrder(row.orderID)}
                                                            >
                                                                {isOpen ? 'Hide Order' : 'Show Order'}
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>#{row.orderID}</Typography>
                                                            {row.tableLabel && (
                                                                <Typography variant="caption" color="text.secondary">Table: {row.tableLabel}</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{row.entDate ? dayjs(row.entDate).format('DD-MMM-YYYY') : '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {row.createdAt ? dayjs(row.createdAt).format('hh:mm A') : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={isFoodpanda ? 'Foodpanda' : String(row.orderType || '').toUpperCase()}
                                                                color={isFoodpanda ? 'warning' : 'primary'}
                                                                variant={isFoodpanda ? 'filled' : 'outlined'}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={String(row.orderStatus || 'N/A').toUpperCase()}
                                                                color={String(row.orderStatus || '').toLowerCase() === 'completed' ? 'success' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">KOT: {row.kotNumber || 'N/A'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">Ticket: {row.ticketNo || 'N/A'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{row.customerName || '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{row.customerPhone || '-'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{row.paymentMethods || 'N/A'}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                            {amount(row.grandTotal)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {row.lastBillPrintedAt
                                                                    ? dayjs(row.lastBillPrintedAt).format('DD-MMM hh:mm A')
                                                                    : 'Not printed'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={10} sx={{ py: 0 }}>
                                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1, m: 1 }}>
                                                                    <Grid2 container spacing={1}>
                                                                        <Grid2 size={{ xs: 12, md: 3 }}>
                                                                            <Typography variant="caption" color="text.secondary">Created At</Typography>
                                                                            <Typography variant="body2">{row.createdAt ? dayjs(row.createdAt).format('DD-MMM-YYYY hh:mm:ss A') : '-'}</Typography>
                                                                        </Grid2>
                                                                        <Grid2 size={{ xs: 12, md: 3 }}>
                                                                            <Typography variant="caption" color="text.secondary">Completed At</Typography>
                                                                            <Typography variant="body2">{row.completedAt ? dayjs(row.completedAt).format('DD-MMM-YYYY hh:mm:ss A') : '-'}</Typography>
                                                                        </Grid2>
                                                                        <Grid2 size={{ xs: 12, md: 3 }}>
                                                                            <Typography variant="caption" color="text.secondary">Aggregator</Typography>
                                                                            <Typography variant="body2">{row.aggregatorPlatform || '-'}</Typography>
                                                                        </Grid2>
                                                                        <Grid2 size={{ xs: 12, md: 3 }}>
                                                                            <Typography variant="caption" color="text.secondary">Aggregator Order #</Typography>
                                                                            <Typography variant="body2">{row.aggregatorOrderCode || '-'}</Typography>
                                                                        </Grid2>
                                                                    </Grid2>

                                                                    <Box sx={{ mt: 1.5 }}>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                                            Items Detail
                                                                        </Typography>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell>Item Code</TableCell>
                                                                                    <TableCell>Item Name</TableCell>
                                                                                    <TableCell align="right">Qty</TableCell>
                                                                                    <TableCell align="right">Rate</TableCell>
                                                                                    <TableCell align="right">Discount</TableCell>
                                                                                    <TableCell align="right">Amount</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {(row.items || []).length > 0 ? (
                                                                                    (row.items || []).map((item, itemIndex) => (
                                                                                        <TableRow key={`${row.orderID}-${item.orderItemID || itemIndex}`}>
                                                                                            <TableCell>{item.itemCode || '-'}</TableCell>
                                                                                            <TableCell>{item.itemName || '-'}</TableCell>
                                                                                            <TableCell align="right">{Number(item.quantity || 0).toLocaleString('en-US')}</TableCell>
                                                                                            <TableCell align="right">{Number(item.unitPrice || 0).toLocaleString('en-US')}</TableCell>
                                                                                            <TableCell align="right">{Number(item.lineDiscount || 0).toLocaleString('en-US')}</TableCell>
                                                                                            <TableCell align="right">{Number(item.itemTotal || 0).toLocaleString('en-US')}</TableCell>
                                                                                        </TableRow>
                                                                                    ))
                                                                                ) : (
                                                                                    <TableRow>
                                                                                        <TableCell colSpan={6} align="center">
                                                                                            No item details found for this order.
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                )}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </Box>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })}

                                        {!loading && visibleRows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center">
                                                    No records found for selected filters.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={reportRows.length}
                                page={page}
                                onPageChange={(_event, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </Box>
                    </Paper>
                </Box>
            </Container>
            <Dialog
                open={printPreviewOpen}
                onClose={handleClosePrintPreview}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Thermal Print Preview</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        80mm thermal layout preview. Yahin se print ya PDF download kar sakte hain.
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Box
                            component="pre"
                            sx={{
                                m: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: '"Courier New", monospace',
                                fontSize: 12,
                                lineHeight: 1.4
                            }}
                        >
                            {thermalPreviewText}
                        </Box>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePrintPreview}>Close</Button>
                    <Button variant="outlined" startIcon={<DownloadForOfflineIcon />} onClick={handleDownloadThermalPdf}>
                        Download PDF
                    </Button>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintThermal}>
                        Print 80mm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SalesReport;
